// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Core whiteboard op-log types (WB-001). The on-the-wire serialization and the
// control ops (enable_annotation / restrict_drawing / snapshot…) are layered on
// top of these by WB-002 — this module stays transport-agnostic and only knows
// about the drawing primitives the engine renders.

/**
 * A single point of a stroke. Coordinates are **normalized** to the unit square
 * `[0,1]` so an op renders identically across canvases of different pixel sizes
 * (see {@link normToPixel} / {@link pixelToNorm}). `w` is an optional per-point
 * width hint (reserved for pressure/stylus in WB-016; unused in v1).
 */
export interface Point {
  x: number;
  y: number;
  w?: number;
}

/** Fields common to every op. */
export interface BaseOp {
  /** Globally-unique op id; targets of erase/undo reference it. */
  id: string;
  /**
   * Which board this op belongs to (see {@link roomBoardId} / {@link screenBoardId}).
   * Lets a Mode-A screen-annotation board and a Mode-B room whiteboard share one call's
   * transport without cross-contaminating: the host routes an op to the engine whose
   * board_id matches. Stamped by the surface on send; optional on the wire (a call with a
   * single board can omit it).
   */
  board_id?: string;
  /**
   * Author of the op. The engine stamps this from its configured identity for
   * locally-produced ops; on receive the **transport** layer re-stamps it from
   * the authenticated peer identity — it is never trusted from the payload
   * (anti-spoof invariant, mirrors `handleSignal`). See WB-002.
   */
  author: string;
  /** Creation timestamp (ms since epoch). */
  ts: number;
}

/** A freehand pen stroke. `highlight` renders it wide + semi-transparent (WB-012). */
export interface StrokeOp extends BaseOp {
  op: "stroke";
  /** CSS color string applied as the stroke style. */
  color: string;
  /** Ordered, normalized points. A single point renders as a dot. */
  points: Point[];
  /** Highlighter mode: wide, semi-transparent (WB-012). */
  highlight?: boolean;
}

/** A geometric shape between two normalized corners/endpoints (WB-012). */
export interface ShapeOp extends BaseOp {
  op: "shape";
  kind: "line" | "rect" | "ellipse" | "arrow";
  color: string;
  from: Point;
  to: Point;
}

/** A free text label anchored at a normalized point (WB-012). */
export interface TextOp extends BaseOp {
  op: "text";
  color: string;
  at: Point;
  text: string;
  /** Font size in reference units (default applied by the renderer). */
  size?: number;
}

/** A sticky note: a filled card with text, anchored at a normalized point (WB-012). */
export interface StickyOp extends BaseOp {
  op: "sticky";
  color: string; // the note's background
  at: Point;
  text: string;
}

/** Removes one or more previously-applied ops by id (erase / undo). */
export interface EraseOp extends BaseOp {
  op: "erase";
  targets: string[];
}

/** Clears the whole board. */
export interface ClearOp extends BaseOp {
  op: "clear";
}

/** A renderable item the engine paints (everything except erase/clear control). */
export type DrawOp = StrokeOp | ShapeOp | TextOp | StickyOp;

/** The union of every op the engine applies to its op-log. */
export type Op = DrawOp | EraseOp | ClearOp;

/** True if `op` is a renderable draw op (stroke/shape/text/sticky). */
export function isDrawOp(op: { op: string }): op is DrawOp {
  return op.op === "stroke" || op.op === "shape" || op.op === "text" || op.op === "sticky";
}

// ── Wire-only ops (WB-002) ───────────────────────────────────────────────────
// These ride the transport but are NOT applied directly to the engine's op-log:
// a `batch` is unwrapped by the transport into its inner ops; control ops are
// handled by the call/UI layer (annotation gate, moderation, late-join sync).

/**
 * Bundles several ops into one wire payload so a transport can flush a coalesced
 * queue in a single frame (the engine already coalesces pointer samples into one
 * stroke op; this batches whole ops). The transport unwraps it and applies each
 * inner op individually.
 */
export interface BatchOp extends BaseOp {
  op: "batch";
  ops: Op[];
}

/**
 * Presenter toggle for Mode A: may viewers draw on this screen-share board?
 * Authored by the presenter for their own `board_id` and enforced client-side
 * (WB-008).
 */
export interface EnableAnnotationOp extends BaseOp {
  op: "enable_annotation";
  /** The screen board this applies to (see {@link screenBoardId}). */
  board_id?: string;
  enabled: boolean;
}

/**
 * Operator moderation for Mode B: restrict drawing to members at or above a
 * power level (0 = anyone, 10 = Voice+, 50 = Operator+). Enforced client-side and,
 * on the SFU path, server-side too (WB-015).
 */
export interface RestrictDrawingOp extends BaseOp {
  op: "restrict_drawing";
  min_power: number;
}

/** Late-joiner / reconnecter asks an existing peer for the current board (WB-006). */
export interface SnapshotRequestOp extends BaseOp {
  op: "snapshot_request";
  board_id?: string;
}

/** Reply to a {@link SnapshotRequestOp}: the current vector op-set (WB-006). */
export interface SnapshotOp extends BaseOp {
  op: "snapshot";
  board_id?: string;
  ops: Op[];
}

/** Control ops — handled by the call/UI layer, not the engine op-log. */
export type ControlOp =
  | EnableAnnotationOp
  | RestrictDrawingOp
  | SnapshotRequestOp
  | SnapshotOp;

/** Everything that can travel over a whiteboard transport. */
export type WireOp = Op | BatchOp | ControlOp;

/** A pixel/logical size used for coordinate mapping. */
export interface Size {
  width: number;
  height: number;
}

/** Clamp a coordinate into the unit interval `[0,1]`. */
export function clampUnit(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Convert a device/CSS-pixel coordinate (relative to the surface's top-left)
 * into a normalized `[0,1]` point. A zero-sized surface maps everything to the
 * origin rather than producing `NaN`.
 */
export function pixelToNorm(px: number, py: number, size: Size): Point {
  const x = size.width > 0 ? px / size.width : 0;
  const y = size.height > 0 ? py / size.height : 0;
  return { x: clampUnit(x), y: clampUnit(y) };
}

/**
 * Convert a normalized point back into device pixels for a surface of the given
 * size. Round-trips with {@link pixelToNorm} for in-range inputs.
 */
export function normToPixel(p: Point, size: Size): { px: number; py: number } {
  return { px: p.x * size.width, py: p.y * size.height };
}
